import { useState, useEffect } from 'react';
import { useGameStore } from '../store/useGameStore';
import { COLOR_CSS, COLOR_LABELS, ELEMENT_CSS, ELEMENT_LABELS, RESONANCE_LABELS, SPIRIT_SKILL_LABELS, SPIRIT_SKILL_DESCS, generateRandomAttrs, generateCraftSkill } from '../data/attributes';
import { SHAPE_DEFS } from '../data/shapes';
import type { Stone, StoneColor, StoneShape, StoneType, AttrEntry, ElementType, ResonanceSkill, ElementAttr } from '../types';
import './StoneEditor.css';

interface StoneEditorProps {
    onClose: () => void;
    editStone?: Stone;
}

const SHAPE_OPTIONS: { value: StoneShape; label: string }[] = [
    { value: 'T', label: 'T形' },
    { value: 'TIAN', label: '田形' },
    { value: 'L_LEFT', label: '左L形' },
    { value: 'L_RIGHT', label: '右L形' },
    { value: 'I', label: 'I形(1×4)' },
];

const COLOR_OPTIONS: { value: StoneColor; label: string }[] = [
    { value: 'white', label: '白色', desc: '1条属性' },
    { value: 'green', label: '绿色', desc: '2条属性' },
    { value: 'blue', label: '蓝色', desc: '3条属性' },
    { value: 'purple', label: '紫色', desc: '4条属性' },
];

const ELEMENT_OPTIONS: { value: ElementType; label: string }[] = [
    { value: 'fire', label: '🔥 天火' },
    { value: 'ice', label: '❄️ 玄冰' },
    { value: 'wood', label: '🌿 苍木' },
    { value: 'thunder', label: '⚡ 神雷' },
];

/* 白色属性池：可供选择的白色属性 */
const WHITE_ATTR_OPTIONS = [
    { key: '会心', label: '会心', cat: 'advanced' as const },
    { key: '专精', label: '专精', cat: 'advanced' as const },
    { key: '调息', label: '调息', cat: 'advanced' as const },
    { key: '元御', label: '元御', cat: 'advanced' as const },
];

/* 元素属性池：增强/抗性 */
const ELEMENT_ATTR_OPTIONS: { key: string; label: string; elem: ElementType; eAttr: ElementAttr }[] = [];
for (const elem of ['fire', 'ice', 'wood', 'thunder'] as ElementType[]) {
    for (const eAttr of ['enhance', 'resist'] as ElementAttr[]) {
        const label = `${ELEMENT_LABELS[elem]}${eAttr === 'enhance' ? '增强' : '抵抗'}`;
        ELEMENT_ATTR_OPTIONS.push({ key: label, label, elem, eAttr });
    }
}

/* 特效词条池（灵蕴共鸣） */
const RESONANCE_OPTIONS = Object.entries(RESONANCE_LABELS).map(([key, label]) => ({
    key,
    label,
    elem: (key.startsWith('fire') ? 'fire' : key.startsWith('ice') ? 'ice' : key.startsWith('wood') ? 'wood' : 'thunder') as ElementType,
}));

/* 颜色对应的属性需求说明 */
const COLOR_ATTR_RULES: Record<StoneColor, { count: number; desc: string }> = {
    white: { count: 1, desc: '1条白色属性（会心/专精/调息/元御）' },
    green: { count: 2, desc: '1条白色属性 + 1条特效词条' },
    blue: { count: 3, desc: '1条白色属性 + 1条特效词条 + 1条元素增强/抗性' },
    purple: { count: 4, desc: '1条白色属性 + 1条特效词条 + 1条元素增强/抗性 + 1条特效词条' },
};

let attrIdCounter = 1000;
function nextId(): string { return `attr_${++attrIdCounter}`; }

export function StoneEditor({ onClose, editStone }: StoneEditorProps) {
    const { addStone } = useGameStore();

    const [type, setType] = useState<StoneType>(editStone?.type || 'skill');
    const [shape, setShape] = useState<StoneShape>(editStone?.shape || 'T');
    const [color, setColor] = useState<StoneColor>(editStone?.color || 'white');
    const [craftElement, setCraftElement] = useState<ElementType>('fire');
    const [skillElement, setSkillElement] = useState<ElementType>(editStone?.element || 'fire');
    const [customAttrs, setCustomAttrs] = useState<AttrEntry[]>(editStone?.attributes || []);
    const [activeTab, setActiveTab] = useState<'random' | 'select'>('random');

    const rule = COLOR_ATTR_RULES[color];
    const colorHex = COLOR_CSS[color];

    // 计算当前已选属性的构成统计
    const whiteCount = customAttrs.filter(a => a.category === 'advanced').length;
    const resonanceCount = customAttrs.filter(a => a.category === 'resonance' && !a.spiritSkill).length;
    const elementCount = customAttrs.filter(a => a.category === 'element').length;

    // 根据规则，各类型需要多少条
    const needsWhite = color === 'white' ? 1 : 1; // 所有颜色都需要至少1条白色
    const needsResonance = color === 'green' || color === 'blue' ? 1 : color === 'purple' ? 2 : 0;
    const needsElement = color === 'blue' || color === 'purple' ? 1 : 0;

    const canConfirm = type === 'craft' || customAttrs.length === rule.count;

    // 切换到随机属性标签时自动生成
    useEffect(() => {
        if (activeTab === 'random' && type === 'skill') {
            const newAttrs = generateRandomAttrs(color, skillElement);
            setCustomAttrs(newAttrs);
        }
    }, [activeTab, color, type, skillElement]);

    // ===== 随机属性 =====
    const handleReRandom = () => {
        const newAttrs = generateRandomAttrs(color, skillElement);
        setCustomAttrs(newAttrs);
    };

    // ===== 手动选择属性 =====
    const handleAddWhite = (label: string) => {
        if (customAttrs.length >= rule.count) return;
        // 白色属性最多选1条
        if (customAttrs.some(a => a.category === 'advanced')) return;
        // 不允许重复
        if (customAttrs.some(a => a.label === label)) return;
        setCustomAttrs([...customAttrs, {
            id: nextId(),
            label,
            category: 'advanced',
            value: 1,
        }]);
    };

    const handleAddResonance = (key: string, label: string, elem: ElementType) => {
        if (customAttrs.length >= rule.count) return;
        // 特效词条：绿色/蓝色最多1条，紫色最多2条
        const currentResonanceCount = customAttrs.filter(a => a.category === 'resonance' && !a.spiritSkill).length;
        const maxResonance = color === 'purple' ? 2 : 1;
        if (currentResonanceCount >= maxResonance) return;
        // 不允许重复特效
        if (customAttrs.some(a => a.resonance === key)) return;
        setCustomAttrs([...customAttrs, {
            id: nextId(),
            label,
            category: 'resonance',
            value: 1,
            element: elem,
            resonance: key as ResonanceSkill,
        }]);
    };

    const handleAddElement = (label: string, elem: ElementType, eAttr: ElementAttr) => {
        if (customAttrs.length >= rule.count) return;
        // 元素增强/抗性最多选1条
        if (customAttrs.some(a => a.category === 'element')) return;
        // 不允许重复
        if (customAttrs.some(a => a.label === label)) return;
        setCustomAttrs([...customAttrs, {
            id: nextId(),
            label,
            category: 'element',
            value: 1,
            element: elem,
            elementAttr: eAttr,
        }]);
    };

    const handleRemoveAttr = (idx: number) => {
        setCustomAttrs(customAttrs.filter((_, i) => i !== idx));
    };

    const handleConfirm = () => {
        if (type === 'craft') {
            const craftSkill = generateCraftSkill(craftElement);
            addStone('craft', 'TIAN', color, craftElement, [craftSkill]);
        } else {
            addStone('skill', shape, color, skillElement, customAttrs);
        }
        onClose();
    };

    const shapePreview = SHAPE_DEFS[shape];
    const previewMatrix = shapePreview.rotations[0];

    return (
        <div className="stone-editor-overlay" onClick={onClose}>
            <div className="stone-editor" onClick={e => e.stopPropagation()}>
                <div className="editor-header">
                    <h3>{editStone ? '编辑石头' : '录入新石头'}</h3>
                    <button className="editor-close" onClick={onClose}>✕</button>
                </div>

                <div className="editor-body">
                    {/* 石头类型 */}
                    <div className="editor-field">
                        <label>石头类型</label>
                        <div className="type-toggle">
                            <button className={`toggle-btn ${type === 'skill' ? 'active' : ''}`}
                                    onClick={() => { setType('skill'); setCustomAttrs([]); }}>
                                机巧石
                            </button>
                            <button className={`toggle-btn ${type === 'craft' ? 'active' : ''}`}
                                    onClick={() => { setType('craft'); setShape('TIAN'); setCustomAttrs([]); }}>
                                匠心石
                            </button>
                        </div>
                    </div>

                    {/* 形状选择 */}
                    {type === 'skill' ? (
                        <div className="editor-field">
                            <label>形状</label>
                            <div className="shape-grid">
                                {SHAPE_OPTIONS.map(opt => (
                                    <button key={opt.value}
                                            className={`shape-btn ${shape === opt.value ? 'active' : ''}`}
                                            onClick={() => setShape(opt.value)}
                                            style={shape === opt.value ? { borderColor: colorHex, boxShadow: `0 0 8px ${colorHex}44` } : {}}>
                    <span className="shape-preview">
                      {SHAPE_DEFS[opt.value].rotations[0].map((row, ri) => (
                          <span key={ri} className="shape-row">
                          {row.map((cell, ci) => (
                              <span key={ci} className={`shape-cell ${cell ? 'filled' : 'empty'}`}
                                    style={cell && shape === opt.value ? { background: colorHex } : {}} />
                          ))}
                        </span>
                      ))}
                    </span>
                                        <span className="shape-label">{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="editor-field">
                            <label>形状（固定）</label>
                            <div className="craft-shape-display">
                <span className="shape-preview">
                  {SHAPE_DEFS.TIAN.rotations[0].map((row, ri) => (
                      <span key={ri} className="shape-row">
                      {row.map((cell, ci) => (
                          <span key={ci} className="shape-cell filled" style={{ background: colorHex }} />
                      ))}
                    </span>
                  ))}
                </span>
                                <span className="shape-label">匠心石 · 田形（2×2）</span>
                            </div>
                        </div>
                    )}

                    {/* 颜色选择 */}
                    <div className="editor-field">
                        <label>颜色</label>
                        <div className="color-grid">
                            {COLOR_OPTIONS.map(opt => (
                                <button key={opt.value}
                                        className={`color-btn ${color === opt.value ? 'active' : ''}`}
                                        onClick={() => { setColor(opt.value); setCustomAttrs([]); }}
                                        style={{
                                            background: `linear-gradient(135deg, ${COLOR_CSS[opt.value]}33, ${COLOR_CSS[opt.value]}15)`,
                                            borderColor: color === opt.value ? COLOR_CSS[opt.value] : 'rgba(255,255,255,0.15)',
                                            boxShadow: color === opt.value ? `0 0 10px ${COLOR_CSS[opt.value]}44` : 'none',
                                        }}>
                                    <span className="color-dot" style={{ background: COLOR_CSS[opt.value] }} />
                                    <span className="color-label">{opt.label}</span>
                                    <span className="color-desc">{opt.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 匠心石灵蕴元素 */}
                    {type === 'craft' && (
                        <div className="editor-field">
                            <label>灵蕴元素</label>
                            <div className="element-grid">
                                {ELEMENT_OPTIONS.map(opt => (
                                    <button key={opt.value}
                                            className={`element-btn ${craftElement === opt.value ? 'active' : ''}`}
                                            onClick={() => setCraftElement(opt.value)}
                                            style={{
                                                borderColor: craftElement === opt.value ? ELEMENT_CSS[opt.value] : 'rgba(255,255,255,0.15)',
                                                boxShadow: craftElement === opt.value ? `0 0 10px ${ELEMENT_CSS[opt.value]}44` : 'none',
                                            }}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <div className="craft-skill-preview">
                <span className="skill-name">
                  {SPIRIT_SKILL_LABELS[`${craftElement}_skill` as keyof typeof SPIRIT_SKILL_LABELS]}
                </span>
                                <span className="skill-desc">
                  {SPIRIT_SKILL_DESCS[`${craftElement}_skill` as keyof typeof SPIRIT_SKILL_DESCS]}
                </span>
                            </div>
                        </div>
                    )}

                    {/* 机巧石属性前缀 */}
                    {type === 'skill' && (
                        <div className="editor-field">
                            <label>属性前缀 <span className="attr-count-hint">（决定灵蕴共鸣和元素属性的归属）</span></label>
                            <div className="element-grid">
                                {ELEMENT_OPTIONS.map(opt => (
                                    <button key={opt.value}
                                            className={`element-btn ${skillElement === opt.value ? 'active' : ''}`}
                                            onClick={() => setSkillElement(opt.value)}
                                            style={{
                                                borderColor: skillElement === opt.value ? ELEMENT_CSS[opt.value] : 'rgba(255,255,255,0.15)',
                                                boxShadow: skillElement === opt.value ? `0 0 10px ${ELEMENT_CSS[opt.value]}44` : 'none',
                                            }}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 机巧石属性录入 */}
                    {type === 'skill' && (
                        <div className="editor-field">
                            <label>
                                属性配置
                                <span className="attr-count-hint">
                  （{rule.desc}）
                </span>
                            </label>

                            {/* 已选属性列表 */}
                            <div className="attr-list">
                                {customAttrs.map((attr, i) => (
                                    <div key={attr.id} className="attr-tag"
                                         style={{ borderLeftColor: attr.element ? ELEMENT_CSS[attr.element] : colorHex }}>
                    <span className="attr-tag-icon">
                      {attr.element === 'fire' ? '🔥' : attr.element === 'ice' ? '❄️' : attr.element === 'wood' ? '🌿' : attr.element === 'thunder' ? '⚡' : '◆'}
                    </span>
                                        <span className="attr-tag-label">{attr.label}</span>
                                        <span className="attr-tag-value">+1</span>
                                        <button className="attr-tag-remove" onClick={() => handleRemoveAttr(i)}>✕</button>
                                    </div>
                                ))}
                                {customAttrs.length === 0 && (
                                    <div className="attr-empty-hint">请选择属性生成方式</div>
                                )}
                            </div>
                            <div className="attr-progress">
                                {customAttrs.length} / {rule.count} 条
                            </div>

                            {/* 两种生成方式 */}
                            <div className="attr-gen-tabs">
                                <button className={`gen-tab ${activeTab === 'random' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('random')}>
                                    🎲 随机属性
                                </button>
                                <button className={`gen-tab ${activeTab === 'select' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('select')}>
                                    📋 选择属性
                                </button>
                            </div>

                            {activeTab === 'random' ? (
                                <div className="attr-random-section">
                                    <p className="random-hint">已按颜色规则自动生成 {rule.count} 条属性</p>
                                    <button className="btn btn-random-all" onClick={handleReRandom}
                                            style={{ background: `linear-gradient(135deg, ${colorHex}, ${colorHex}88)` }}>
                                        🔄 重新随机
                                    </button>
                                </div>
                            ) : (
                                <div className="attr-select-section">
                                    {/* 白色属性 */}
                                    <div className="select-group">
                                        <span className="select-group-label">白色属性 {whiteCount}/{needsWhite}</span>
                                        <div className="select-options">
                                            {WHITE_ATTR_OPTIONS.map(opt => {
                                                const hasWhite = customAttrs.some(a => a.category === 'advanced');
                                                const disabled = customAttrs.length >= rule.count || hasWhite || customAttrs.some(a => a.label === opt.label);
                                                return (
                                                    <button key={opt.key} className={`select-option ${disabled ? 'disabled' : ''}`}
                                                            disabled={disabled} onClick={() => handleAddWhite(opt.label)}
                                                            style={{ borderColor: colorHex }}>
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* 特效词条 — 按属性前缀筛选 */}
                                    {(color === 'green' || color === 'blue' || color === 'purple') && (
                                        <div className="select-group">
                      <span className="select-group-label">
                        特效词条（灵蕴共鸣）
                          {color === 'purple' ? ` ${resonanceCount}/2` : ` ${resonanceCount}/${needsResonance}`}
                      </span>
                                            <div className="select-options grid-4">
                                                {RESONANCE_OPTIONS.filter(opt => opt.elem === skillElement).map(opt => {
                                                    const maxResonance = color === 'purple' ? 2 : 1;
                                                    const resCount = customAttrs.filter(a => a.category === 'resonance' && !a.spiritSkill).length;
                                                    const disabled = customAttrs.length >= rule.count || resCount >= maxResonance || customAttrs.some(a => a.resonance === opt.key);
                                                    return (
                                                        <button key={opt.key} className={`select-option resonance ${disabled ? 'disabled' : ''}`}
                                                                disabled={disabled}
                                                                onClick={() => handleAddResonance(opt.key, opt.label, opt.elem)}
                                                                style={{ borderLeftColor: ELEMENT_CSS[opt.elem] }}>
                              <span className="elem-icon">
                                {opt.elem === 'fire' ? '🔥' : opt.elem === 'ice' ? '❄️' : opt.elem === 'wood' ? '🌿' : '⚡'}
                              </span>
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* 元素增强/抗性 — 按属性前缀筛选 */}
                                    {(color === 'blue' || color === 'purple') && (
                                        <div className="select-group">
                                            <span className="select-group-label">元素增强/抗性 {elementCount}/{needsElement}</span>
                                            <div className="select-options grid-4">
                                                {ELEMENT_ATTR_OPTIONS.filter(opt => opt.elem === skillElement).map(opt => {
                                                    const hasElement = customAttrs.some(a => a.category === 'element');
                                                    const disabled = customAttrs.length >= rule.count || hasElement || customAttrs.some(a => a.label === opt.label);
                                                    return (
                                                        <button key={opt.key} className={`select-option element ${disabled ? 'disabled' : ''}`}
                                                                disabled={disabled}
                                                                onClick={() => handleAddElement(opt.label, opt.elem, opt.eAttr)}
                                                                style={{ borderLeftColor: ELEMENT_CSS[opt.elem] }}>
                              <span className="elem-icon">
                                {opt.elem === 'fire' ? '🔥' : opt.elem === 'ice' ? '❄️' : opt.elem === 'wood' ? '🌿' : '⚡'}
                              </span>
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="editor-footer">
                    <button className="btn btn-cancel" onClick={onClose}>取消</button>
                    <button className={`btn btn-confirm ${!canConfirm ? 'disabled' : ''}`}
                            disabled={!canConfirm}
                            onClick={handleConfirm}
                            style={canConfirm ? { background: `linear-gradient(135deg, ${colorHex}, ${colorHex}88)` } : {}}>
                        确认录入{type === 'craft' ? '' : ` (${customAttrs.length}/${rule.count})`}
                    </button>
                </div>
            </div>
        </div>
    );
}